import { useCallback, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import type { HeaderOptionItem } from './desktopContext';
import { HeaderBar, type Breadcrumb } from './HeaderBar';
import { Sidebar } from './Sidebar';
import './desktop.css';
import { visibleNavGroups } from './navigation';
import {
  ACCOUNT_GROUP_TITLE,
  ACCOUNT_PASSWORD_LABEL,
  ACCOUNT_PASSWORD_PATH,
  findModuleRoute,
} from './routes';
import { getConfiguredApiBaseUrl } from '../lib/apiBase';
import { usePermissions, type UsePermissionsOptions } from '../lib/permissions/usePermissions';

export interface DesktopShellProps {
  /** inject ได้เพื่อการทดสอบ — production ใช้ค่าจาก `VITE_API_BASE_URL` */
  readonly permissionsOptions?: UsePermissionsOptions;
  readonly initialSidebarExpanded?: boolean;
}

/** ปลายทางร่วมของ crumb ที่ไม่มีหน้าของตัวเอง (องค์กร/กลุ่มเมนู) — ตัวเดียวกับหน้าแรก */
const HOME_PATH = '/';

const HOME_BREADCRUMBS: readonly Breadcrumb[] = [
  { label: 'CAP ERP', to: HOME_PATH },
  { label: 'หน้าแรก', to: HOME_PATH },
  { label: 'ภาพรวมระบบ' },
];

/**
 * เปลี่ยนรหัสผ่านไม่ได้มาจากเมนู จึงไม่มี `groupTitle`/`label` ให้หยิบจาก route
 * registry — ถ้าไม่ระบุไว้ที่นี่ หน้าจอนี้จะใช้ breadcrumb ของหน้าแรกซึ่งโกหกว่า
 * ผู้ใช้กำลังอยู่ที่ "ภาพรวมระบบ"
 */
const ACCOUNT_PASSWORD_BREADCRUMBS: readonly Breadcrumb[] = [
  { label: 'CAP ERP', to: HOME_PATH },
  { label: ACCOUNT_GROUP_TITLE, to: HOME_PATH },
  { label: ACCOUNT_PASSWORD_LABEL },
];

/** trailing crumb ที่ยังไม่มีหน้าจอไหน contribute — ผูกกับ path ว่างซึ่งไม่มีจริง */
const NO_TRAILING_CRUMBS: TrailingCrumbState = { path: '', crumbs: [] };

interface TrailingCrumbState {
  readonly path: string;
  readonly crumbs: readonly Breadcrumb[];
}

/** รายการเมนู "ตัวเลือก" ที่ยังไม่มีหน้าจอไหน contribute — ผูกกับ path ว่างซึ่งไม่มีจริง */
const NO_HEADER_OPTION_ITEMS: HeaderOptionItemsState = { path: '', items: [] };

interface HeaderOptionItemsState {
  readonly path: string;
  readonly items: readonly HeaderOptionItem[];
}

/**
 * Layout route ของ Desktop ERP
 *
 * โหลดสิทธิ์ครั้งเดียวแล้วส่งลงให้ทั้งเมนูและหน้าจอลูกผ่าน `<Outlet />` เมนูที่
 * แสดงกับ route ที่ผ่าน guard จึงถูกตัดสินจาก permission set ชุดเดียวกันเสมอ
 * ไม่มีทางที่เมนูจะโชว์รายการที่ route ปฏิเสธ หรือกลับกัน
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer — backend ยังเป็นผู้บังคับสิทธิ์จริงทุกคำขอ
 */
export function DesktopShell({
  permissionsOptions,
  initialSidebarExpanded = true,
}: DesktopShellProps = {}) {
  const [sidebarExpanded, setSidebarExpanded] = useState(initialSidebarExpanded);
  const [trailing, setTrailing] = useState<TrailingCrumbState>(NO_TRAILING_CRUMBS);
  const [headerOptionItemsState, setHeaderOptionItemsState] = useState<HeaderOptionItemsState>(
    NO_HEADER_OPTION_ITEMS,
  );
  const permissions = usePermissions(permissionsOptions ?? {});
  const apiBaseUrl = permissionsOptions?.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const { pathname } = useLocation();

  const groups = visibleNavGroups(permissions);
  const activeRoute = findModuleRoute(pathname);
  const baseCrumbs: readonly Breadcrumb[] =
    activeRoute !== undefined
      ? [
          { label: 'CAP ERP', to: HOME_PATH },
          { label: activeRoute.groupTitle, to: HOME_PATH },
          // crumb ของแม่ (D-137) — มีเฉพาะ route ที่เป็นลูกที่ path ต่างจากแม่
          ...(activeRoute.parent === undefined
            ? []
            : [{ label: activeRoute.parent.label, to: activeRoute.parent.path }]),
          // `to` ชี้ path เปล่าของหน้าจอเสมอ (ไม่มี search) — คลิกจาก sub-page ใด ๆ
          // จึงล้าง `?view=...`/`?product_id=...` กลับไปที่รายการเปล่าเสมอ ไม่ต้อง
          // สนใจว่าตอนนี้ crumb นี้เป็นตัวสุดท้ายหรือไม่ เพราะ `HeaderBar` เป็นผู้
          // ตัดสินว่า crumb ตัวสุดท้ายไม่เป็นลิงก์ ไม่ใช่ที่นี่
          { label: activeRoute.label, to: activeRoute.path },
        ]
      : pathname === ACCOUNT_PASSWORD_PATH
        ? ACCOUNT_PASSWORD_BREADCRUMBS
        : HOME_BREADCRUMBS;

  /**
   * crumb ท้ายสุดที่หน้าจอลูก contribute เอง — ผูกกับ `pathname` ที่ตั้งค่าไว้
   * ตอนนั้น ไม่ใช่ค่าปัจจุบัน ป้องกัน crumb ของหน้าจอเดิมหลุดค้างมาโผล่บนหน้าจอใหม่
   * ตอนเปลี่ยนเส้นทาง โดยไม่ต้องพึ่งลำดับ effect ระหว่าง parent (ที่นี่) กับ
   * หน้าจอลูกซึ่งเชื่อถือไม่ได้ (React รัน effect ของลูกก่อนพ่อเสมอ — ถ้า parent
   * ใช้ effect ล้างค่าตาม `pathname` มันจะล้างค่าที่ลูกเพิ่งตั้งไปหมาด ๆ ในคอมมิต
   * เดียวกัน)
   */
  const trailingCrumbs = trailing.path === pathname ? trailing.crumbs : [];
  const breadcrumbs: readonly Breadcrumb[] = [...baseCrumbs, ...trailingCrumbs];

  const setTrailingCrumbs = useCallback(
    (crumbs: readonly Breadcrumb[]) => {
      setTrailing({ path: pathname, crumbs });
    },
    [pathname],
  );

  // ผูกกับ pathname ตอนลงทะเบียน (ไม่ใช่ pathname ปัจจุบัน) เหตุผลเดียวกับ `trailingCrumbs` ข้างบน
  const headerOptionItems =
    headerOptionItemsState.path === pathname ? headerOptionItemsState.items : [];

  const setHeaderOptionItems = useCallback(
    (items: readonly HeaderOptionItem[]) => {
      setHeaderOptionItemsState({ path: pathname, items });
    },
    [pathname],
  );

  const toggleSidebar = () => {
    setSidebarExpanded((current) => !current);
  };

  return (
    <div className="cap-shell" data-testid="desktop-shell">
      <Sidebar expanded={sidebarExpanded} groups={groups} onToggle={toggleSidebar} />
      <main className="cap-main">
        <HeaderBar
          breadcrumbs={breadcrumbs}
          sidebarExpanded={sidebarExpanded}
          onToggleSidebar={toggleSidebar}
          permissions={permissions}
          pageOptionItems={headerOptionItems}
        />
        <div className="cap-content">
          <Outlet
            context={{ permissions, apiBaseUrl, setTrailingCrumbs, setHeaderOptionItems }}
          />
        </div>
      </main>
    </div>
  );
}
